import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { API, useAuth } from "@/App";
import SEO, { pageSEO } from "@/components/SEO";
import LazyImage from "@/components/LazyImage";

const OAUTH_ERRORS = {
  google_auth_failed: "Google sign-in was cancelled or failed. Please try again.",
  token_exchange_failed: "Google authentication failed. Please try again.",
  userinfo_failed: "Could not retrieve your Google account info. Please try again.",
  oauth_error: "Google sign-in encountered an error. Please try again.",
  no_email: "No email found on your Google account.",
  no_code: "Google sign-in was incomplete. Please try again.",
  disposable_email: "Temporary email addresses are not allowed. Please use a permanent email (Gmail, Outlook, etc.).",
};

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const error = searchParams.get("error");
    if (error && OAUTH_ERRORS[error]) {
      toast.error(OAUTH_ERRORS[error]);
    } else if (error) {
      toast.error("Sign-in failed. Please try again.");
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "include"
      });

      if (response.ok) {
        const data = await response.json();
        console.log("[Login] Success, storing token...");
        // Store token
        if (data.access_token) {
          localStorage.setItem("syce_token", data.access_token);
          console.log("[Login] Token stored:", localStorage.getItem("syce_token")?.substring(0, 20));
        }
        toast.success("Welcome back!");
        console.log("[Login] Redirecting to dashboard...");
        // Use window.location for a full page navigation to ensure fresh state
        window.location.href = "/dashboard";
        return; // Prevent any further execution
      } else {
        const error = await response.json();
        toast.error(error.detail || "Invalid credentials");
      }
    } catch (error) {
      toast.error("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    const origin = window.location.origin;
    const url = `${API}/auth/google/login?redirect_origin=${encodeURIComponent(origin)}`;
    
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);
        
        const response = await fetch(url, { signal: controller.signal });
        clearTimeout(timeout);
        
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.auth_url;
          return;
        }
        
        if (attempt < 2) continue;
        
        const errorData = await response.json().catch(() => ({}));
        console.error("Google login error:", response.status, errorData);
        if (errorData.detail && errorData.detail.includes("not configured")) {
          toast.error("Google login is temporarily unavailable. Please use email login.");
        } else {
          toast.error("Failed to initialize Google login. Please try email login.");
        }
      } catch (error) {
        if (attempt < 2) continue;
        console.error("Google login network error:", error);
        toast.error("Connection error. Please check your internet and try again.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <SEO {...pageSEO.login} />
      
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <LazyImage 
              src="https://customer-assets.emergentagent.com/job_royalty-revolution/artifacts/gc921mlg_IMG_1214.png" 
              alt="SYCE.me" 
              className="h-12 w-auto"
            />
          </Link>

          <h1 className="font-heading text-3xl font-bold mb-2" data-testid="login-title">Log in to SYCE</h1>
          <p className="text-muted-foreground mb-8">
            Don't have an account?{" "}
            <Link to="/register" className="text-primary hover:underline" data-testid="register-link">Sign up</Link>
          </p>

          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 mb-6 border-border hover:bg-white/5"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="artist@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 bg-input/50"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-12 bg-input/50"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" className="rounded border-border" />
                <span className="text-muted-foreground">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-primary hover:underline">Forgot password?</Link>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-bold"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full"></span>
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:flex flex-1 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1593697725250-6a184f4e9ed0?w=1200&q=80)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background to-transparent" />
        <div className="absolute inset-0 bg-primary/10" />
      </div>
    </div>
  );
}
